"use client";

import React from "react";
import { Phone, Shield, Clock, Star, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
function CompanionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Quote Request Sent</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/50 hover:bg-primary/10 rounded-lg">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="bg-background/50 border-border/50 focus-visible:ring-secondary/50 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Enter your full legal name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@ottawa-mail.com" className="bg-background/50 border-border/50 focus-visible:ring-secondary/50 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">We'll send your free quote to this address.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(613) 555-0123" className="bg-background/50 border-border/50 focus-visible:ring-secondary/50 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Best number for a quick text or call.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="address" rules={{
        required: "Address is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Service Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Elgin St, Ottawa" className="bg-background/50 border-border/50 focus-visible:ring-secondary/50 rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Where should our team arrive?</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="event_date" rules={{
        required: "Pickup Date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Preferred Pickup Date</FormLabel>
              <FormControl>
                <Input type="date" className="bg-background/50 border-border/50 focus-visible:ring-secondary/50 block w-full rounded-lg" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Select your ideal removal date.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="junk_items" rules={{
        required: "Please describe your items"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Junk Items</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-border/50 focus-visible:ring-secondary/50 w-full rounded-lg">
                    <SelectValue placeholder="What are we hauling?" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["Furniture Removal", "Appliance Disposal", "Construction Debris", "Estate Cleanout", "Other / Mixed Junk"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription className="text-xs opacity-70">Choose the primary category of items.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-default">Notes & Photo Upload</FormLabel>
            <FormControl>
              <Textarea placeholder="List specific items or provide a link to photos for a more accurate quote..." className="resize-none bg-background/50 border-primary/30 border-dashed focus-visible:ring-secondary/50 min-h-[120px] rounded-lg" {...field} />
            </FormControl>
            <FormDescription className="text-xs opacity-70">Optional: Mention stairs, heavy items, or specific access instructions.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-bold bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all shadow-lg rounded-lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Calculating Quote...
          </> : 'Get My Free Quote + Availability Check'}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
        <Shield className="size-3.5" />
        Locally owned, fully insured, and eco-friendly disposal guaranteed.
      </p>
    </Form>;
}
export default function SecureYourCompanion() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const lifestyleImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_junk_removal_team.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_junk_removal_team.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_junk_removal_team.png-wvc-sizes",
    alt: "JUNKR team smiling beside trailer in Ottawa"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_clean_garage_after.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_clean_garage_after.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_clean_garage_after.png-wvc-sizes",
    alt: "Before and after clean organized garage space"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_loaded_trailer_organized.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_loaded_trailer_organized.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_loaded_trailer_organized.png-wvc-sizes",
    alt: "Organized JUNKR trailer loaded with furniture for disposal"
  }];
  return <section data-section-id="241608" id="schedule-your-pickup" className="bg-gradient-to-br from-primary to-background text-foreground py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-lg bg-secondary/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <div className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary/10 border border-secondary/20 text-secondary text-sm font-bold tracking-wide">
                <Star className="size-4 fill-secondary/50" />
                <span>Ottawa's Local Choice</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-bold leading-tight text-foreground">
                Get Your Free Quote + <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-secondary/70">
                  Same-Day Availability Check
                </span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-default">
                Fast, friendly junk removal across Kanata, Nepean, Orleans, and the greater Ottawa area. No obligation, transparent pricing, and a free estimate.
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border/50 group">
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent z-10 pointer-events-none" />
              
              <Carousel className="w-full" opts={{
              loop: true
            }}>
                <CarouselContent>
                  {lifestyleImages.map((image, i) => <CarouselItem key={i} data-index={i}>
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    </CarouselItem>)}
                </CarouselContent>
                <div className="absolute top-1/2 -translate-y-1/2 left-4 z-30 opacity-0 group-hover:opacity-100 transition-opacity">
                  <CarouselPrevious className="relative left-0 translate-x-0 bg-background/20 hover:bg-background/40 border-none text-white" />
                </div>
                <div className="absolute top-1/2 -translate-y-1/2 right-4 z-30 opacity-0 group-hover:opacity-100 transition-opacity">
                  <CarouselNext className="relative right-0 translate-x-0 bg-background/20 hover:bg-background/40 border-none text-white" />
                </div>
              </Carousel>
              
              <div className="absolute bottom-0 left-0 w-full p-6 z-20">
                <Button asChild variant="secondary" className="w-full h-14 text-lg font-bold rounded-xl shadow-lg bg-secondary text-secondary-foreground hover:bg-secondary/90">
                  <Link to="tel:+16135550123" className="flex items-center justify-center gap-3">
                    <Phone className="size-5" />
                    Text us photos for instant pricing
                  </Link>
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3">
                <Clock className="size-5 text-secondary/70" />
                <span className="text-sm text-muted-foreground font-default">Same-Day Service</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="size-5 text-secondary/70" />
                <span className="text-sm text-muted-foreground font-default">Fully Insured</span>
              </div>
            </div>
          </div>

          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="bg-card/40 backdrop-blur-xl border border-border/50 rounded-3xl p-6 sm:p-10 shadow-2xl relative">
              <div className="absolute top-0 left-0 w-16 h-16 border-t border-l border-secondary/30 rounded-tl-3xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b border-r border-secondary/30 rounded-br-3xl pointer-events-none" />
              
              <div className="mb-8">
                <p className="text-secondary font-bold text-sm mb-2">We’ll respond within 2 hours.</p>
                <h3 className="text-2xl font-default font-bold text-foreground mb-2">Get Your Free Quote</h3>
                <p className="text-sm text-muted-foreground font-default">No obligation, transparent pricing, and a free estimate. Provide your details and we'll get back to you with a fair price.</p>
              </div>

              <FormProvider formKey="schedule_pickup_form_home" formId="schedule_pickup_form_home" sectionName="Schedule Your Pickup" defaultValues={{
              full_name: "",
              email: "samburland@gmail.com",
              phone: "",
              address: "",
              event_date: "",
              junk_items: "",
              additional_details: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              address: {
                required: true
              },
              event_date: {
                required: true
              },
              junk_items: {
                required: true
              }
            }} submitText="Get My Free Quote + Availability Check" successMessage="Your quote request is in! We'll text you within 2 hours with pricing and availability. Check your email for details." errorMessage="There was an error submitting your request. Please try again or text us photos directly.">
                <CompanionForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}